import {
  useMemo,
  useState,
  useCallback,
  type ComponentPropsWithoutRef,
} from 'react';
import { useTracking } from 'react-tracking';
import { reverse } from 'lodash/fp';
import { format } from 'date-fns';
import loadable from '@loadable/component';
import { useMarketPerformance } from '../../hooks/useMarketPerformance';
import type { PeriodType } from '../../query/fetchMarketPerformance';
import { MarketPerformanceChart } from './MarketPerformanceChart';
import MarketDataTable from './MarketDataTable';
import ChartBottom from './ChartBottom';
import type { Visibility } from './styled';
import If from '@components/If';
import { getReadableCountry } from '@/utilities/markets';
import { formatterFactor } from '../../utils/formatter';
import useReduxStateMarket from '../../hooks/useReduxStateMarkets';

const LearnMoreModal = loadable(() => import('../LearnMoreModal'));
const MarketValuationChartModal = loadable(
  () => import('../LearnMoreModal/MarketValuationChartModal')
);

function isPeriodType(value: string | number): value is PeriodType {
  if (typeof value !== 'string') return false;
  return ['1Y', '3Y', '5Y', '10Y'].includes(value);
}
const MarketPerformance = () => {
  const { market, sector, secondary } = useReduxStateMarket();
  const { trackEvent } = useTracking();
  const [period, setPeriod] = useState<PeriodType>('3Y');
  const [tableVisibility, setTableVisibility] = useState<Visibility>('hidden');
  const [learnMoreModalVisibility, setLearnMoreModalVisibility] =
    useState<boolean>(false);

  const { data, status } = useMarketPerformance(period);
  const { chartData, industry, averages } = data;
  const toggleTableVisibility = useCallback(() => {
    setTableVisibility(tableVisibility === 'hidden' ? 'visible' : 'hidden');
    trackEvent({
      action: 'click',
      subject: 'market-performance-data-table',
    });
  }, [tableVisibility, trackEvent]);
  const openLearnMoreModal = useCallback(() => {
    setLearnMoreModalVisibility(true);
    trackEvent({
      action: 'click',
      subject: 'market-performance-learn-more-modal',
    });
  }, [trackEvent]);

  const handlePeriodSelect = useCallback<
    ComponentPropsWithoutRef<
      typeof MarketPerformanceChart
    >['handlePeriodSelect']
  >((value) => {
    if (isPeriodType(value)) {
      setPeriod(value);
      trackEvent({
        type: 'track',
        action: 'click',
        subject: 'market-performance-period-selection',
        data: value,
      });
    }
  }, []);

  const tableData = useMemo(
    () => reverse(chartData).filter((v, i) => i % 3 === 0),
    [chartData]
  );
  return (
    <>
      <MarketDataTable data={tableData} tableVisibility={tableVisibility} />
      <MarketPerformanceChart
        chartData={chartData}
        averages={averages}
        handlePeriodSelect={handlePeriodSelect}
        period={period}
        status={status}
        isShowingTable={tableVisibility === 'visible'}
      />
      <ChartBottom
        tableVisibility={tableVisibility}
        isButtonDisabled={chartData.length === 0}
        toggleTableVisibility={toggleTableVisibility}
        openLearnMoreModal={openLearnMoreModal}
      />
      <If match={learnMoreModalVisibility && chartData.length > 0}>
        {() => {
          const averagePe3y = averages.medianPE['3Y'];
          const averagePe10y = averages.medianPE['10Y'];
          const latestData = chartData[chartData.length - 1];
          return (
            <LearnMoreModal
              close={() => setLearnMoreModalVisibility(false)}
              title={`About the ${
                (secondary && 'Industry') ||
                (sector && 'Sector') ||
                (market && 'Market')
              } Valuation and Performance Chart`}
            >
              <MarketValuationChartModal
                market={getReadableCountry(market)}
                industry={industry}
                latestAbsolutePE={latestData.absolutePEFormatted}
                latestPS={latestData.psFormatted}
                latestMedianPE={latestData.medianPEFormatted}
                analysisDate={format(latestData.time, 'MMM dd yyyy')}
                textMarketOrIndustry={!sector ? 'market' : 'industry'}
                fullMarketSectorIndustry={`${getReadableCountry(market)} ${
                  !sector
                    ? 'Market'
                    : `${industry} ${secondary ? 'Industry' : 'Sector'}`
                }`}
                averagePe3y={formatterFactor(averagePe3y)}
                averagePe10y={formatterFactor(averagePe10y)}
                marketCap={latestData.marketCapFormatted}
                earnings={latestData.earningsFormatted}
                revenue={latestData.revenueFormatted}
              />
            </LearnMoreModal>
          );
        }}
      </If>
    </>
  );
};

export default MarketPerformance;
